import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Upload } from "lucide-react";

interface SpreadChangeFormProps {
  loanNoteId: string;
  onSuccess: () => void;
  onCancel: () => void;
}

export const SpreadChangeForm = ({ loanNoteId, onSuccess, onCancel }: SpreadChangeFormProps) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState<string>("");
  const [formData, setFormData] = useState({
    new_spread_percentage: "",
    new_spread_date: "",
    apply_from_date: "",
    change_approval_proof_url: "",
  });

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingFile(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${loanNoteId}-spread-proof-${Date.now()}.${fileExt}`;
      const filePath = `spread-proofs/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('loan-agreements')
        .upload(filePath, file, {
          contentType: file.type,
          upsert: false,
          metadata: {
            originalFileName: file.name
          }
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('loan-agreements')
        .getPublicUrl(filePath);

      setUploadedFileName(file.name);
      setFormData({ ...formData, change_approval_proof_url: publicUrl });
      toast({ title: "File uploaded successfully" });
    } catch (error) {
      console.error("Error uploading file:", error);
      toast({
        title: "Error",
        description: "Failed to upload file",
        variant: "destructive",
      });
    } finally {
      setUploadingFile(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await supabase
        .from("spread_changes")
        .insert({
          loan_note_id: loanNoteId,
          new_spread_percentage: parseFloat(formData.new_spread_percentage),
          new_spread_date: formData.new_spread_date,
          apply_from_date: formData.apply_from_date,
          change_approval_proof_url: formData.change_approval_proof_url || null,
        });

      if (error) throw error;

      toast({ title: "Spread change recorded successfully" });
      onSuccess();
    } catch (error) {
      console.error("Error recording spread change:", error);
      toast({
        title: "Error",
        description: "Failed to record spread change",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = () => {
    return (
      formData.new_spread_percentage !== "" &&
      formData.new_spread_date !== "" &&
      formData.apply_from_date !== ""
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label htmlFor="new_spread_percentage">New Spread %</Label>
        <Input
          id="new_spread_percentage"
          type="number"
          step="0.01"
          value={formData.new_spread_percentage}
          onChange={(e) => setFormData({ ...formData, new_spread_percentage: e.target.value })}
          required
        />
      </div>

      <div>
        <Label htmlFor="new_spread_date">New Spread Date</Label>
        <Input
          id="new_spread_date"
          type="date"
          value={formData.new_spread_date}
          onChange={(e) => setFormData({ ...formData, new_spread_date: e.target.value })}
          required
        />
      </div>

      <div>
        <Label htmlFor="apply_from_date">Apply from Date</Label>
        <Input
          id="apply_from_date"
          type="date"
          value={formData.apply_from_date}
          onChange={(e) => setFormData({ ...formData, apply_from_date: e.target.value })}
          required
        />
      </div>

      <div>
        <Label htmlFor="change_approval_proof">Change Approval Proof</Label>
        <div className="space-y-2">
          <Input
            id="change_approval_proof_file"
            type="file"
            onChange={handleFileUpload}
            disabled={uploadingFile}
            className="hidden"
            accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => document.getElementById('change_approval_proof_file')?.click()}
            disabled={uploadingFile}
            className="w-full"
          >
            <Upload className="h-4 w-4 mr-2" />
            {uploadingFile ? "Uploading..." : "Upload Approval Proof"}
          </Button>
          {uploadedFileName && (
            <div className="text-sm text-muted-foreground bg-muted/50 px-3 py-2 rounded-md">
              <span className="font-medium">File uploaded:</span> {uploadedFileName}
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button type="submit" disabled={loading || !isFormValid()}>
          {loading ? "Saving..." : "Record Spread Change"}
        </Button>
      </div>
    </form>
  );
};
